import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateTeam,
  addTeamMember,
  removeTeamMember,
  fetchTeams
} from "../../../../redux/slices/teamSlice";
import { fetchUsers } from "../../../../redux/slices/userSlice";
import { getFullName } from "../../../../utils/helpers";
import { getProfilePictureUrl } from "../../../../utils/imageUtils";
import ViewTeamModal from "../ViewTeamModal/ViewTeamModal";
import EditTeamModal from "../EditTeamModal/EditTeamModal";

const mapMembers = (team) => {
  if (!team || !team.memberships) return [];
  return team.memberships
    .filter((m) => !m.left_at)
    .map((m) => ({
      id: m.user?.id || m.user_id,
      name: getFullName(m.user) || m.user?.username || "N/A",
      role: typeof m.user?.role === "string" ? m.user.role : m.user?.role?.name || "",
      pic: getProfilePictureUrl(m.user),
      user: m.user
    }));
};

const ViewEditTeamModal = ({ team: initialTeam, onUpdate, onClose }) => {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users?.items || []);

  const [isEditing, setIsEditing] = useState(false);
  const [team, setTeam] = useState(initialTeam);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (users.length === 0) {
      dispatch(fetchUsers());
    }
  }, [dispatch, users.length]);

  useEffect(() => {
    setTeam(initialTeam);
    setIsEditing(false);
    setError(null);
  }, [initialTeam]);

  const members = mapMembers(team);

  const handleSave = useCallback(async ({ name, description, members: newMembers }) => {
    try {
      setError(null);
      await dispatch(updateTeam({ id: team.id, name, description })).unwrap();

      const originalMemberUserIds = members.map((m) => m.id);
      const newMemberUserIds = newMembers.map((m) => m.id || m.user_id);

      const membersToAdd = newMemberUserIds.filter((id) => !originalMemberUserIds.includes(id));
      const membersToRemove = originalMemberUserIds.filter((id) => !newMemberUserIds.includes(id));

      for (const userId of membersToRemove) {
        await dispatch(removeTeamMember({ teamId: team.id, userId })).unwrap();
      }

      for (const userId of membersToAdd) {
        await dispatch(addTeamMember({ teamId: team.id, userId })).unwrap();
      }

      await dispatch(fetchTeams()).unwrap();
      
      if (onUpdate) onUpdate();
      setIsEditing(false);
    } catch (err) {
      setError("Error updating team");
      console.error(err);
    }
  }, [dispatch, team, members, onUpdate]);

  return (
    <>
      {error && <div className="text-danger mb-2 p-3">{error}</div>}
      {isEditing ? (
        <EditTeamModal
          initialName={team.name}
          initialDescription={team.description}
          initialMembers={members}
          onSave={handleSave}
          onCancel={() => setIsEditing(false)}
        />
      ) : (
        <ViewTeamModal
          team={{ ...team, members }}
          onEdit={() => setIsEditing(true)}
          onClose={onClose}
        />
      )}
    </>
  );
};

export default ViewEditTeamModal;